import React, { FunctionComponent } from 'react'
import { useIntl } from 'react-intl'
import { TouchableOpacity, View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { AuroraAICoachAvatarIcon } from '../../atoms/icons/AuroraAICoachAvatarIcon'
import { Avatar, avatarAltTextGeneric, AvatarDetails } from '../../atoms/icons/Avatar'
import { NumberBadge } from '../../atoms/icons/NumberBadge'
import { TextBadge } from '../../atoms/icons/TextBadge'
import { BadgeVariant } from '../../badge/constants'
import { ListItem } from '../../molecules/listItem/ListItem'
import { BodyTextSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { getFriendlyFormattedTimeStamp, tID } from '../../utils/utils'

/**
 * This component represents a single MessageThread in a threadlist
 * Each MessageThread is a conversation with a provider
 */

export type MessageThreadProps = {
  avatarDetails: AvatarDetails
  threadTitleMsg: string
  threadSubTitleMsg: string
  threadMsg: string
  conversationId: string
  textBadgeMsg?: string
  timeStampMsg: string
  unreadMsgCount: number
  onThreadPressed: (conversationId: string) => void
  isSidebarView?: boolean
  isSelected?: boolean
  isAICoach?: boolean
}

const AvatarContainer = styled.View<{ theme: ThemeType; isSidebarView: boolean }>(({ theme, isSidebarView }) => ({
  justifyContent: isSidebarView ? 'center' : 'initial',
  margin: isSidebarView
    ? `${theme.spacing['16px']} ${theme.spacing['12px']} ${theme.spacing['16px']} ${theme.spacing['16px']}`
    : theme.spacing['24px'],
}))

const ThreadContent = styled.View<{ theme: ThemeType; isSidebarView: boolean }>(({ theme, isSidebarView }) => ({
  marginVertical: theme.spacing['24px'],
  marginRight: theme.spacing['16px'],
  width: '224px',
  flexShrink: 1,
  ...(isSidebarView && { marginVertical: 'auto', flex: 1, width: '156px' }),
}))

const ThreadTitle = styled(BodyText)({
  width: '180px',
})

const MessageSubtitleContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: '2px',
  marginBottom: theme.spacing['4px'],
  flexDirection: 'row',
}))

const ThreadMessage = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  height: theme.spacing['40px'],
}))

const TextBadgeContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  position: 'absolute',
  marginTop: theme.spacing['24px'],
  right: theme.spacing['24px'],
}))

const NumberBadgeContainer = styled.View<{ theme: ThemeType; isSidebarView: boolean }>(({ theme, isSidebarView }) => ({
  ...(isSidebarView
    ? {
        justifyContent: 'center',
        marginRight: theme.spacing['16px'],
      }
    : {
        position: 'absolute',
        marginTop: '65px',
        right: theme.spacing['24px'],
      }),
}))

const SelectedIndicator = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: '50%',
  height: '64px',
  width: '3px',
  backgroundColor: theme.colors.borderActive,
  borderTopRightRadius: '2px',
  borderBottomRightRadius: '2px',
}))

export const MessageThread: FunctionComponent<MessageThreadProps> = ({
  avatarDetails,
  threadTitleMsg,
  threadSubTitleMsg,
  threadMsg,
  onThreadPressed,
  textBadgeMsg,
  timeStampMsg,
  conversationId,
  unreadMsgCount,
  isSidebarView = false,
  isSelected,
  isAICoach = false,
  children,
}) => {
  const showTimeStamp = timeStampMsg && textBadgeMsg !== undefined
  const intl = useIntl()
  const { colors, isAuroraTheme } = useTheme() as ThemeType
  const avatarSize = isSidebarView ? 38 : 48

  return (
    <View testID={tID('MessageThread')}>
      <TouchableOpacity onPress={() => onThreadPressed(conversationId)}>
        <ListItem
          height={isSidebarView ? 90 : undefined}
          dividerWidth={isSidebarView ? '100%' : '90%'}
          dividerColor={isAuroraTheme ? colors.borderDefault : undefined}
          {...(isSelected && {})}
        >
          {isSidebarView && isSelected && <SelectedIndicator style={{ transform: [{ translateY: '-50%' }] }} />}
          <AvatarContainer isSidebarView={isSidebarView}>
            {isAICoach && isAuroraTheme ? (
              <AuroraAICoachAvatarIcon size={avatarSize} />
            ) : (
              <Avatar
                details={avatarDetails}
                accessibilityLabel={intl.formatMessage(avatarAltTextGeneric, { name: avatarDetails.displayName })}
                size={avatarSize}
              />
            )}
          </AvatarContainer>
          <ThreadContent isSidebarView={isSidebarView}>
            <ThreadTitle text={threadTitleMsg} numberOfLines={1} />
            {!isSidebarView && (
              <>
                <MessageSubtitleContainer>
                  {threadSubTitleMsg && (
                    <BodyText text={threadSubTitleMsg} size={BodyTextSize.SMALL} color={colors.textSecondary} />
                  )}
                  {Boolean(showTimeStamp) && (
                    <BodyText
                      /* getFriendlyFormattedTimeStamp handles internationalization */
                      /* eslint-disable-next-line formatjs/no-literal-string-in-jsx */
                      text={` \u2022 ${getFriendlyFormattedTimeStamp(intl, timeStampMsg)}`}
                      size={BodyTextSize.SMALL}
                      color={colors.textSecondary}
                      testID={tID('MessageThread-timestamp')}
                    />
                  )}
                </MessageSubtitleContainer>
                <ThreadMessage text={threadMsg} numberOfLines={2} size={BodyTextSize.SMALL} />
              </>
            )}
          </ThreadContent>
          {!isSidebarView && Boolean(textBadgeMsg) && (
            <TextBadgeContainer>
              <TextBadge text={textBadgeMsg ?? ''} variant={BadgeVariant.ALERT} />
            </TextBadgeContainer>
          )}
          {unreadMsgCount > 0 && (
            <NumberBadgeContainer testID={tID('MessageThread-unreadMsgCount')} isSidebarView={isSidebarView}>
              <NumberBadge number={String(unreadMsgCount)} />
            </NumberBadgeContainer>
          )}
          {children}
        </ListItem>
      </TouchableOpacity>
    </View>
  )
}

export default MessageThread
